import { useMemo, useState } from "react";
import {
  X, Loader2, AlertCircle, CheckCircle2, ScrollText, Download, FileWarning,
} from "lucide-react";
import {
  citationArchiveName,
  citationFileBase,
  exportApplicantCitations,
  planApplicantCitations,
  type CitationExportReport,
} from "../utils/exportApplicantCitations";

/**
 * Download every fully endorsed fellowship candidate's citation in one zip.
 *
 * The dialog exists rather than a bare button for the same reasons the
 * photograph one does: this is a long-running job over the network, and a
 * button that simply goes quiet for a minute reads as broken. It also has
 * something to report at the end — whose citation is missing — which an
 * automatic download has nowhere to say.
 *
 * Nothing here is written back to the server; it reads the files the list
 * already carries links to.
 */
export default function ExportApplicantCitationsModal({
  rows, categoryName, onClose,
}: {
  /** The fully endorsed rows, exactly as the list has them. */
  rows: any[];
  categoryName: string;
  onClose: () => void;
}) {
  const plan = useMemo(() => planApplicantCitations(rows), [rows]);

  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [report, setReport] = useState<CitationExportReport | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const running = progress !== null && report === null && failure === null;

  const run = async () => {
    setFailure(null);
    setReport(null);
    setProgress({ done: 0, total: plan.jobs.length });
    try {
      const result = await exportApplicantCitations(
        plan,
        { category: categoryName },
        (done, total) => setProgress({ done, total }),
      );
      setReport(result);
    } catch (e: any) {
      setFailure(e?.message ?? "The citations could not be downloaded.");
    }
  };

  const pct = progress?.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50"
      onClick={running ? undefined : onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-amber-600" />
              Export {categoryName} candidates' citations
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              The citation lodged by every fully endorsed candidate, as one zip.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            className="text-gray-400 hover:text-gray-700 disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          {/* ── After the download ────────────────────────────────────────── */}
          {report ? (
            <>
              <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm font-semibold">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  {report.saved} citation{report.saved === 1 ? "" : "s"} downloaded
                  {" · "}
                  {(report.bytes / 1024 / 1024).toFixed(1)} MB
                  <div className="font-mono text-[11px] font-normal mt-1 break-all">
                    {report.filename}
                  </div>
                </div>
              </div>

              {report.failed.length > 0 && (
                <Gaps
                  tone="warn"
                  title={`Could not be downloaded (${report.failed.length})`}
                  rows={report.failed.map((f) => ({
                    key: String(f.applicationId),
                    text: `${f.member} — ${f.reason}`,
                  }))}
                />
              )}

              {report.missing.length > 0 && (
                <Gaps
                  title={`No citation on file (${report.missing.length})`}
                  rows={report.missing.map((m) => ({
                    key: String(m.applicationId),
                    text: m.member,
                  }))}
                />
              )}

              <p className="text-xs text-gray-500">
                The archive holds a <b>manifest.csv</b> listing every file and every gap, so it
                still says what it is a month from now.
              </p>
            </>
          ) : null}

          {/* ── While it runs ─────────────────────────────────────────────── */}
          {running ? (
            <div>
              <div className="flex items-center justify-between text-sm font-semibold text-gray-700">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                  Downloading citations…
                </span>
                <span className="tabular-nums text-gray-500">
                  {progress?.done} / {progress?.total}
                </span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Keep this dialog open — the zip is built here in the browser and is only saved
                once every citation has been collected.
              </p>
            </div>
          ) : null}

          {failure ? (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {failure}
            </div>
          ) : null}

          {/* ── Before the download ───────────────────────────────────────── */}
          {!report && !running ? (
            rows.length === 0 ? (
              <div className="p-4 rounded-xl border bg-gray-50 text-sm text-gray-700">
                No application in this category is fully endorsed yet. A candidate appears here
                once their chapter has accepted and both referees have approved.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <Figure label="Candidates" value={String(plan.candidates)} />
                  <Figure
                    label="Citation on file"
                    value={`${plan.withCitation} of ${plan.candidates}`}
                    tone={plan.withNone ? "warn" : "good"}
                  />
                  <Figure label="Files in the zip" value={String(plan.jobs.length)} tone="good" />
                </div>

                {plan.withNone > 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                    {plan.withNone} candidate{plan.withNone === 1 ? " has" : "s have"} lodged no
                    citation. A citation is step 9 of the fellowship application, so this is a gap
                    in their paperwork rather than in the export — they are listed below by name.
                  </p>
                ) : null}

                <div className="text-xs text-gray-600 bg-gray-50 border rounded-xl px-4 py-3 space-y-1">
                  <div>
                    Files are named <b>Member Name_Membership No</b> — the same name their
                    photograph carries, so the two archives sort alike:
                  </div>
                  <div className="font-mono text-[11px] text-gray-500">
                    {citationFileBase(rows[0])}.pdf
                  </div>
                  <div className="pt-1">
                    A citation may be a PDF or a scan, so the archive holds both.
                  </div>
                  <div>
                    Saved as <span className="font-mono">{citationArchiveName(categoryName)}</span>
                  </div>
                </div>

                {plan.missing.length > 0 && (
                  <Gaps
                    tone="warn"
                    title={`No citation on file (${plan.missing.length}) — these cannot be exported`}
                    rows={plan.missing.map((m) => ({
                      key: String(m.applicationId),
                      text: m.member,
                    }))}
                  />
                )}
              </>
            )
          ) : null}
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={running}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-40"
          >
            {report ? "Close" : "Cancel"}
          </button>
          {!report && (
            <button
              onClick={run}
              disabled={running || plan.jobs.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download {plan.jobs.length || ""} file{plan.jobs.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Figure({
  label, value, tone = "plain",
}: {
  label: string;
  value: string;
  tone?: "plain" | "good" | "warn";
}) {
  const tones = {
    plain: "bg-white border-gray-200 text-gray-900",
    good: "bg-emerald-50 border-emerald-100 text-emerald-800",
    warn: "bg-amber-50 border-amber-100 text-amber-800",
  } as const;

  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</div>
      <div className="text-lg font-black mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

/** Who is not in the archive, and why — collapsed, but never omitted. */
function Gaps({
  title, rows, tone = "plain",
}: {
  title: string;
  rows: { key: string; text: string }[];
  tone?: "plain" | "warn";
}) {
  return (
    <details
      className={`rounded-xl border px-3 py-2 ${
        tone === "warn" ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-gray-50"
      }`}
    >
      <summary className="text-xs font-bold text-gray-700 cursor-pointer inline-flex items-center gap-1.5">
        <FileWarning className="w-3.5 h-3.5" />
        {title}
      </summary>
      <ul className="mt-2 space-y-1 text-xs text-gray-600 max-h-40 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.key}>{r.text}</li>
        ))}
      </ul>
    </details>
  );
}
